import './ChangeSubscription.scss'

import { useState, useEffect } from 'react'
import { Button, Container, Col, Form, Row, Spinner } from 'react-bootstrap'

import Error from '../../common/error/Error'
import { onCancelationRequest } from 'components/common/api/Support'
import { getAuthenticatedUser } from '../../auth/'
import AccountMenu from 'components/common/navigation/AccountMenu'
import { cancelSubscription } from 'components/common/api/Payments'
import AccountDeleteNotice from '../AccountDeleteNotice'
import {
  EVENT_ACTION_CANCEL,
  EVENT_ACTION_PAGEVIEW,
  EVENT_CATEGORY_ACCOUNT_CHANGE,
  EVENT_CATEGORY_SUBSCRIPTION,
  saveUserEvent
} from 'components/common/api/UserEvents'

const cancellationReasons = [
  { key: 'doing_better', reason: 'I’m doing better and no longer need it' },
  { key: 'content', reason: 'The content isn’t what I expected' },
  { key: 'did_not_meet_needs', reason: 'It didn’t meet my needs' },
  { key: 'too_expensive', reason: 'It’s too expensive' },
  { key: 'did_not_use_it', reason: 'I don’t use it enough' },
  { key: 'other', reason: 'Other (please explain)' }
]

/**
 * Change Subscription component. Used to enable users to modify or cancel their existing subscription
 */
export default function ChangeSubscription({ setHeaderTitle, setReturnPath }) {
  const user = getAuthenticatedUser()
  const [reason, setReason] = useState()
  const [feedback, setFeedback] = useState()
  const [error, setError] = useState()
  const [success, setSuccess] = useState()
  const [loading, setLoading] = useState()

  /**
   * Event handler function
   * @param {Event} event The event that triggered the handler
   */
  const handleCancelClick = async event => {
    event.preventDefault()
    event.stopPropagation()

    // Reset the error and loading states
    setError(null)
    setLoading(true)

    // Cancel the subscription
    const result = await cancelSubscription({ reason, feedback })

    const body = {
      email: user.email,
      reason,
      message: feedback
    }
    const notificationResult = await onCancelationRequest(body)

    // Successfully cancelled the subscription?
    if (result.success && notificationResult.status === 'success')
      setSuccess(result.success)
    else
      setError(
        result.error ??
          'An unknown error occurred. Please contact support to resolve this issue.'
      )

    setLoading(false)

    // Scroll to top
    window.scrollTo(0, 0)

    // Log the cancellation event
    saveUserEvent(EVENT_CATEGORY_SUBSCRIPTION, EVENT_ACTION_CANCEL)
  }

  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0)

    // Update header and return path
    setReturnPath(null)
    setHeaderTitle('Cancel')

    // Log access to the page
    saveUserEvent(EVENT_CATEGORY_ACCOUNT_CHANGE, EVENT_ACTION_PAGEVIEW)
  }, [setReturnPath, setHeaderTitle])

  return (
    <Container className='change-subscription-container'>
      <Row xs={1} md={2}>
        {/* Sidebar panel for larger screens */}
        <Col md={4} className='mobile-hidden account-sidebar'>
          <AccountMenu />
        </Col>
        <Col md={8}>
          <h1 className='title'>Cancel Subscription</h1>
          {/* Show errors if we have any */}
          <Error error={error} />
          {!success ? (
            <>
              <p>
                Thanks for being a part of Remble®! Before you leave, we have
                two quick questions that we hope you'll answer to help us make
                Remble® better.
              </p>
              <Form.Label htmlFor='formReason' className='mt-4'>
                <h5>What is your main reason for canceling?</h5>
              </Form.Label>

              <Form.Group className='form-group' controlId='formReason'>
                <Form.Select
                  name='reason'
                  onChange={e => setReason(e.currentTarget.value)}
                >
                  <option value=''>Select an option...</option>
                  {cancellationReasons.map(option => (
                    <option key={option.key} value={option.key}>
                      {option.reason}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Label htmlFor='formFeedback' className='mt-4'>
                <h5>What can we do better?</h5>
              </Form.Label>
              <Form.Group controlId='formFeedback' className='form-group'>
                <Form.Control
                  disabled={loading}
                  size='lg'
                  placeholder='What can we do better?'
                  required
                  as='textarea'
                  className='feedback-textarea'
                  onChange={e => setFeedback(e.currentTarget.value)}
                />
              </Form.Group>
            </>
          ) : (
            <>
              <p className='success-message'>
                Thank you {user.display_name}! Your subscription has been
                cancelled. At the end of your billing cycle, you'll be
                downgraded to a freemium account and will lose access to Premium
                features and content.
              </p>
              {/* If cancellation reason is too expensive, offer user a discount code after we cancel their subscription. */}
              {reason === 'too_expensive' && (
                <>
                  <hr className='my-4' />
                  <h4>Our goal is to make Remble® accessible to everyone</h4>
                  <p>
                    We’ve tried our best to keep our pricing low by charging
                    less than a typical therapy session for our annual
                    subscription, which comes down to only 25 cents per day
                    (with an annual plan). If you would like to continue using
                    Remble® at a reduced price, use this discount code at
                    checkout to get <strong>50% off</strong> your first year,
                    which takes your yearly price down to $45:
                  </p>
                  <h3 className='text-center my-3 green'>REMBLE50</h3>
                </>
              )}
            </>
          )}
          <Button
            size='lg'
            variant='primary'
            className='w-100 mt-4'
            onClick={handleCancelClick}
            disabled={loading || !reason || !feedback}
            hidden={success}
          >
            <Spinner
              animation='border'
              role='status'
              size='sm'
              style={{ margin: '0 10px 2px 0' }}
              hidden={!loading}
            >
              <span className='visually-hidden'>Loading...</span>
            </Spinner>
            <span>Cancel Subscription</span>
          </Button>

          <AccountDeleteNotice />
        </Col>
      </Row>
    </Container>
  )
}
